import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Era5MonthWeatherReportService } from '@6grain/masofa-monolith-api-client/weather-report';
import { FilterOperator } from '@app/utils/filter-operator';
import { SortType } from '@6grain/masofa-monolith-api-client/common/model/sortType';
import { ChartDateManager } from './chart-date.manager';
import { ChartIndicatorManager } from './chart-indicator.manager';
import {EraWeatherStationService} from '@6grain/masofa-monolith-api-client/weather';

/**
 * Point data for chart
 */
export interface PointData {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

/**
 * Result of point data loading
 */
export interface PointDataResult {
  point: PointData;
  data: number[];
  minYear: number;
  maxYear: number;
  success: boolean;
  error?: string;
}

/**
 * Manager for loading weather data for multiple monitoring points
 */
export class ChartDataLoadManager {
  constructor(
    private weatherService: EraWeatherStationService,
    private era5MonthService: Era5MonthWeatherReportService,
    private dateManager: ChartDateManager,
    private indicatorManager: ChartIndicatorManager
  ) {

  }

  /**
   * Load weather data for a single point
   */
  loadPointData(
    point: PointData,
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[],
    dataSource: string
  ): Observable<PointDataResult> {
    // Only ERA5 data source is supported for now
    if (dataSource !== 'era5') {
      return of(this.createEmptyResult(point, labels, ));
    }

    return this.weatherService.weatherEraWeatherStationGetByCoordinatesGet(point.lat, point.lng).pipe(
      switchMap(station => {
        if (!station || !station.id) {
          return of(this.createEmptyResult(point, labels, 'No weather station found'));
        }

        return this.loadStationData(station.id as string, point, startDate, endDate, indicator, labels);
      }),
      catchError(error => {
        console.error(`Error loading data for point ${point.name}:`, error);
        return of(this.createEmptyResult(point, labels, error.message));
      })
    );
  }

  /**
   * Load weather data for multiple points in parallel
   */
  loadMultiplePointsData(
    points: PointData[],
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[],
    dataSource: string
  ): Observable<PointDataResult[]> {
    if (!points || points.length === 0) {
      return of([]);
    }

    // Only ERA5 data source is supported for now
    if (dataSource !== 'era5') {
      return of(points.map(point => this.createEmptyResult(point, labels, `Data source '${dataSource}' not supported yet`)));
    }

    const requests = points.map(point =>
      this.loadPointData(point, startDate, endDate, indicator, labels, dataSource)
    );

    return forkJoin(requests);
  }

  /**
   * Load data for a specific weather station
   */
  private loadStationData(
    weatherStationId: string,
    point: PointData,
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[]
  ): Observable<PointDataResult> {
    const dateParams = this.dateManager.getDateRangeParams(startDate, endDate);

    const filters = [
      {
        filterField: 'weatherStation',
        filterOperator: FilterOperator.Equals,
        filterValue: weatherStationId
      },
      {
        filterField: 'Year',
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: dateParams.startYear.toString()
      },
      {
        filterField: 'Year',
        filterOperator: FilterOperator.LessThanOrEqual,
        filterValue: dateParams.endYear.toString()
      }
    ];

    const takeCount = Math.max(dateParams.monthsInRange, 100);

    return this.era5MonthService.weatherReportsEra5MonthWeatherReportGetByQueryPost({
      filters: filters,
      offset: 0,
      sortBy: 'Year',
      sort: SortType.NUMBER_0,
      take: takeCount
    }).pipe(
      map(response => this.processResponse(response, point, indicator, labels)),
      catchError(error => {
        console.error(`Error loading station data for ${point.name}:`, error);
        return of(this.createEmptyResult(point, labels, error.message));
      })
    );
  }

  /**
   * Process API response into chart data
   */
  private processResponse(
    response: any[] | null,
    point: PointData,
    indicator: string,
    labels: string[]
  ): PointDataResult {
    if (!response || !Array.isArray(response) || response.length === 0) {
      return this.createEmptyResult(point, labels, 'No data available');
    }

    // Build data map keyed by YYYY-MM
    const dataByKey = new Map<string, any>();
    response.forEach(item => {
      const itemYear = item.year || item.Year;
      const itemMonth = item.month || item.Month;
      if (itemYear != null && itemMonth != null) {
        const key = `${itemYear}-${String(itemMonth).padStart(2, '0')}`;
        dataByKey.set(key, item);
      }
    });

    // Extract values for each label
    const values: number[] = labels.map(label => {
      const monthData = dataByKey.get(label);
      if (monthData) {
        return this.indicatorManager.extractIndicatorValue(monthData, indicator);
      }
      return 0;
    });

    // Calculate year range
    const years = response.map(item => item.year || item.Year).filter(y => y != null);
    const minYear = years.length > 0 ? Math.min(...years) : 0;
    const maxYear = years.length > 0 ? Math.max(...years) : 0;

    return {
      point,
      data: values,
      minYear,
      maxYear,
      success: true
    };
  }

  /**
   * Create an empty result for error cases
   */
  private createEmptyResult(point: PointData, labels: string[], error?: string): PointDataResult {
    return {
      point,
      data: Array(labels.length).fill(0),
      minYear: 0,
      maxYear: 0,
      success: false,
      error
    };
  }
}
